import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Modal, Spinner, Alert } from 'react-bootstrap';
import axios from 'axios';
import { FaEdit, FaTrash, FaImage, FaCheck } from 'react-icons/fa';

const Content1 = ({ isAdmin = false }) => {
    const [contents, setContents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentContent, setCurrentContent] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        image: null
    });
    const [imagePreview, setImagePreview] = useState('');
    const [success, setSuccess] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [imageError, setImageError] = useState(false);

    // Fetch all content from API
    useEffect(() => {
        const fetchContent = async () => {
            try {
                console.log("Fetching content from: https://madhusewingmachines.com/api/content1");
                const response = await axios.get('https://madhusewingmachines.com/api/content1');
                
                if (response.data) {
                    console.log("Content1 data received:", response.data);
                    setImageError(false);
                    
                    if (response.data.image) {
                        console.log("Image filename:", response.data.image);
                        const img = new Image();
                        img.onload = () => console.log("Image preloaded successfully");
                        img.onerror = () => setImageError(true);
                        img.src = `https://madhusewingmachines.com/uploads/${response.data.image}`;
                    } else {
                        setImageError(true);
                    }
                    
                    setContents([response.data]);
                }
            } catch (err) {
                setError('Failed to fetch content');
                console.error('Fetch error:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchContent();
    }, [success]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData(prev => ({
                ...prev,
                image: file
            }));
            setImagePreview(URL.createObjectURL(file));
        }
    };

    const handleEditClick = (content) => {
        setCurrentContent(content);
        setFormData({
            title: content.title,
            description: content.description
        });
        if (content.image) {
            setImagePreview(`https://madhusewingmachines.com/uploads/${content.image}`);
        }
        setShowEditModal(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setIsSubmitting(true);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('title', formData.title);
            formDataToSend.append('description', formData.description);
            
            if (formData.image) {
                console.log("Appending image to form data:", formData.image);
                formDataToSend.append('image', formData.image);
            } else {
                console.log("No new image selected");
            }

            console.log("Submitting to API...");
            let response;
            
            if (currentContent) {
                // Update existing content
                console.log("Updating existing content with ID:", currentContent._id);
                response = await axios.put(
                    'https://madhusewingmachines.com/api/content1',
                    formDataToSend,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            } else {
                // Create new content
                console.log("Creating new content");
                response = await axios.post(
                    'https://madhusewingmachines.com/api/content1',
                    formDataToSend,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    }
                );
            }

            console.log("API response:", response.data);
            setSuccess(currentContent ? 'Content updated successfully!' : 'Content created successfully!');
            setShowEditModal(false);
            setCurrentContent(null);
            setFormData({
                title: '',
                description: '',
                image: null
            });
            setImagePreview('');
            setImageError(false); // Reset image error state
        } catch (err) {
            console.error('Submit error details:', err);
            setError(err.response?.data?.message || 'Failed to save content');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this content?')) {
            try {
                await axios.delete(`https://madhusewingmachines.com/api/content1/${id}`);
                setSuccess('Content deleted successfully!');
                setContents([]); // Clear contents since your backend only handles single content
            } catch (err) {
                setError(err.response?.data?.message || 'Failed to delete content');
                console.error('Delete error:', err);
            }
        }
    };

    if (loading) return (
        <Container fluid className="py-4 text-center">
            <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
            </Spinner>
        </Container>
    );

    if (error) return (
        <Container fluid className="py-4 text-center">
            <Alert variant="danger">{error}</Alert>
        </Container>
    );

    return (
        <Container fluid className="py-5" style={{backgroundColor: "#EFFBFF" }}>
            {/* Admin Controls */}
            {isAdmin && (
                <div className="mb-4 d-flex justify-content-end gap-2">
                    <Button
                        variant="outline-primary"
                        onClick={() => {
                            setCurrentContent(null);
                            setFormData({ title: '', description: '', image: null });
                            setImagePreview('');
                            setShowEditModal(true);
                        }}
                    >
                        <FaEdit className="me-2" />
                        {contents.length > 0 ? 'Update Content' : 'Add Content'}
                    </Button>
                    {contents.length > 0 && (
                        <Button variant="outline-danger" onClick={() => handleDelete(contents[0]._id)}>
                            <FaTrash className="me-2" />
                            Delete
                        </Button>
                    )}
                </div>
            )}

            {success && (
                <Alert variant="success" onClose={() => setSuccess('')} dismissible>
                    <FaCheck className="me-2" /> {success}
                </Alert>
            )}

            {/* Display content */}
            {contents.length > 0 ? (
                <div className="bg-white rounded-4 shadow-lg overflow-hidden">
                    <Row className="g-0">
                        {/* Image Section */}
                        <Col md={5}>
                            <div className="d-flex align-items-center justify-content-center h-100 p-3 bg-light">
                                {!imageError ? (
                                    <img
                                        src={`https://madhusewingmachines.com/uploads/${contents[0].image}`}
                                        alt="Content"
                                        className="img-fluid"
                                        style={{ maxHeight: "250px", objectFit: "contain" }}
                                        onError={() => setImageError(true)}
                                    />
                                ) : (
                                    <div className="text-center">
                                        <FaImage size={40} className="text-secondary mb-2" />
                                        <p className="text-secondary">Image not available</p>
                                    </div>
                                )}
                            </div>
                        </Col>

                        {/* Text Content */}
                        <Col md={7} className="p-5">
                            <h2 className="fw-bold mb-3">{contents[0].title}</h2>
                            <p className="text-muted" style={{ fontSize: "1.1rem" }}>
                                {contents[0].description}
                            </p>
                        </Col>
                    </Row>
                </div>
            ) : (
                <div className="bg-white rounded-4 shadow-sm p-5 text-center">
                    <FaImage size={40} className="text-secondary mb-3" />
                    <h4>No Content Available</h4>
                    <p className="text-muted">
                        {isAdmin ? 'Add content to display here' : 'Check back later for updates'}
                    </p>
                    {isAdmin && (
                        <Button
                            variant="primary"
                            onClick={() => {
                                setCurrentContent(null);
                                setFormData({ title: '', description: '', image: null });
                                setImagePreview('');
                                setShowEditModal(true);
                            }}
                        >
                            <FaEdit className="me-2" />
                            Add Content Now
                        </Button>
                    )}
                </div>
            )}

            {/* Edit/Add Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{currentContent ? 'Update Content' : 'Add New Content'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Title*</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter title"
                                    />
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Description*</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={5}
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        required
                                        placeholder="Enter content description"
                                    />
                                </Form.Group>
                            </Col>

                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Image*</Form.Label>

                                    {imagePreview ? (
                                        <div className="position-relative mb-3">
                                            <img
                                                src={imagePreview}
                                                alt="Preview"
                                                className="img-fluid rounded border"
                                                style={{
                                                    height: "180px",
                                                    width: "100%",
                                                    objectFit: "contain",
                                                    backgroundColor: "#f8f9fa",
                                                    padding: "10px"
                                                }}
                                            />
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                className="position-absolute top-0 end-0 m-2"
                                                onClick={() => {
                                                    setImagePreview('');
                                                    setFormData(prev => ({ ...prev, image: null }));
                                                }}
                                            >
                                                <FaTrash />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="text-center p-3 bg-light rounded border mb-3">
                                            <FaImage size={30} className="text-secondary mb-2" />
                                            <p className="mb-0">No image selected</p>
                                        </div>
                                    )}

                                    <Form.Control
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        required={!currentContent}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="d-flex justify-content-end mt-3">
                            <Button
                                variant="secondary"
                                onClick={() => setShowEditModal(false)}
                                className="me-2"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Saving...
                                    </>
                                ) : currentContent ? 'Update' : 'Save'}
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>
        </Container>
    
    );
};

export default Content1;
